import type { CSSProperties } from "react";
import { colors, radius, spacing } from "../design-system";
import { AppPressable } from "./AppPressable";
import { AppText } from "./AppText";

type BannerTone = "danger" | "warning" | "success" | "info";

type BannerProps = {
  tone?: BannerTone;
  message: string;
  /** Action optionnelle (ex. « Recharger ») rendue en lien à droite. */
  action?: { label: string; onPress: () => void };
  style?: CSSProperties;
};

// Recette « banner doux » (Base) : fond au cran clair, bordure au cran moyen,
// texte au cran 600 lisible (≥ 4.5:1). Le warning porte la famille warn* —
// jamais l'orange de marque.
const TONES: Record<BannerTone, { bg: string; border: string; fg: string }> = {
  danger: { bg: colors.sos200, border: colors.sos400, fg: colors.sos600 },
  warning: { bg: colors.warn50, border: colors.warn300, fg: colors.warn600 },
  success: { bg: colors.success100, border: colors.success500, fg: colors.success600 },
  info: { bg: colors.navy50, border: colors.navy200, fg: colors.navy800 },
};

/**
 * Bandeau de feedback PERSISTANT — le complément du Toast.
 *
 * Règle d'usage : un Toast (3s) pour l'événement ponctuel non bloquant ; un
 * Banner pour l'état qui DURE tant que l'utilisateur n'a pas agi (validation
 * incomplète, solde insuffisant, hors-ligne). Un échec financier dans un toast
 * de 3 secondes est illisible ; un dialogue bloquant est disproportionné.
 *
 * `role="alert"` remplace `accessibilityRole="alert"` : même intention, le
 * bandeau est annoncé dès son apparition.
 */
export function Banner({ tone = "info", message, action, style }: BannerProps) {
  const palette = TONES[tone];

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        border: `1px solid ${palette.border}`,
        borderRadius: radius.input,
        paddingTop: spacing.md,
        paddingBottom: spacing.md,
        paddingLeft: spacing.lg,
        paddingRight: spacing.lg,
        backgroundColor: palette.bg,
        ...style,
      }}
    >
      <AppText variant="small" color={palette.fg} style={{ flex: 1 }}>
        {message}
      </AppText>

      {action && (
        <AppPressable
          onClick={action.onPress}
          aria-label={action.label}
          // `hitSlop` n'existe pas en CSS : on élargit la cible avec du padding
          // négatif en marge, pour ne pas décaler la mise en page tout en
          // gardant une zone cliquable confortable (≥ 44px recommandé).
          style={{ padding: spacing.sm, margin: -spacing.sm }}
          pressedStyle={{ opacity: 0.6 }}
        >
          <AppText variant="smallStrong" color={palette.fg}>
            {action.label}
          </AppText>
        </AppPressable>
      )}
    </div>
  );
}
